// The LIB reports console route subtree. Code-based routes per DEV-GUIDE-002 §4
// — a package-composed subtree hand-writes `.lazy()` + `createLazyRoute`. This
// factory hangs `/library/reports` off the portal's root route so router.tsx
// stays the single wiring point.
//
// GATING: every /reports/* endpoint rides rbac('lib:reports'), so the LAYOUT
// gate matches the API tier exactly — a defence-in-depth mirror (ADR-001 §14.2;
// the UI can only HIDE, the gateway is the authority). No loaders: the pages
// are one-aggregate GETs the screen's useQuery owns (retry lives in the shell).
import { Outlet, createRoute } from "@tanstack/solid-router";
import { lazyRoute } from "@erp/ui/route-kit";
import { ConsoleLayout } from "@erp/ui/console-layout";
import type { AnyRoute } from "@tanstack/solid-router";

function ReportsLayout() {
  return (
    <ConsoleLayout
      perm="lib:reports"
      deniedMessage="This account cannot open reports. Ask an administrator for reports access."
      skeletonId="reports-skeleton"
      deniedId="reports-denied"
      unreachableId="reports-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

export function makeLibReportsRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const reportsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "library/reports",
    component: ReportsLayout,
  });

  const overviewRoute = createRoute({
    getParentRoute: () => reportsRoute,
    path: "/",
  }).lazy(lazyRoute(() => import("../../screens/admin/reports/Overview.js")));

  const circulationRoute = createRoute({
    getParentRoute: () => reportsRoute,
    path: "circulation",
  }).lazy(lazyRoute(() => import("../../screens/admin/reports/Circulation.js")));

  const catalogueRoute = createRoute({
    getParentRoute: () => reportsRoute,
    path: "catalogue",
  }).lazy(lazyRoute(() => import("../../screens/admin/reports/Catalogue.js")));

  const finesRoute = createRoute({
    getParentRoute: () => reportsRoute,
    path: "fines",
  }).lazy(lazyRoute(() => import("../../screens/admin/reports/Fines.js")));

  const membersRoute = createRoute({
    getParentRoute: () => reportsRoute,
    path: "members",
  }).lazy(lazyRoute(() => import("../../screens/admin/reports/Members.js")));

  const acquisitionsRoute = createRoute({
    getParentRoute: () => reportsRoute,
    path: "acquisitions",
  }).lazy(lazyRoute(() => import("../../screens/admin/reports/Acquisitions.js")));

  const digitalRoute = createRoute({
    getParentRoute: () => reportsRoute,
    path: "digital",
  }).lazy(lazyRoute(() => import("../../screens/admin/reports/Digital.js")));

  return [
    reportsRoute.addChildren([
      overviewRoute,
      circulationRoute,
      catalogueRoute,
      finesRoute,
      membersRoute,
      acquisitionsRoute,
      digitalRoute,
    ]),
  ];
}
